import { Icon } from 'Components';
import Heading from 'LearningManager/components/Heading';
import { useStepper } from 'LearningManager/hooks/Stepper.context';
import styles from 'LearningManager/styles/CustomSetup.module.scss';
import { IndividualCoursesData } from 'LearningManager/constants/createAssignment.constant';
import ConfigureSettingsCard from 'LearningManager/components/Review/ConfigureSettingsCard';

type AudienceType =
	| 'By Organization'
	| 'By Property'
	| 'By Role'
	| 'By Group'
	| 'By Individual'
	| 'Not selected';

const organizationOptions = [{ id: 32, title: 'Entire organization' }];
const propertyOptions = [
	{ id: 1, title: 'Pyramid Hotel' },
	{ id: 2, title: 'Marriott Hotel' },
	{ id: 3, title: 'Grand Plaza' },
	{ id: 4, title: 'Ocean View Resort' }
];
const roleOptions = [
	{ id: 1, title: 'Staff' },
	{ id: 2, title: 'Manager' },
	{ id: 3, title: 'Leadership and IT' }
];
const groupOptions = [
	{ id: 1, title: 'Guest Relations' },
	{ id: 2, title: 'Room Service Management' },
	{ id: 3, title: 'Event Coordination' },
	{ id: 4, title: 'Front Desk Operations' },
	{ id: 5, title: 'Facility Maintenance' },
	{ id: 6, title: 'Culinary Services' },
	{ id: 7, title: 'Luxury Experience Coordination' },
	{ id: 8, title: 'Sales Promotions' }
];
const individualOptions = [
	{ id: 1, title: 'Alice Johnson' },
	{ id: 2, title: 'Benjamin Smith' },
	{ id: 3, title: 'Charlotte Brown' },
	{ id: 4, title: 'David Wilson' },
	{ id: 5, title: 'Emily Davis' },
	{ id: 6, title: 'Frank Miller' },
	{ id: 7, title: 'Grace Lee' },
	{ id: 8, title: 'Henry Taylor' }
];

const assignOptionById: Record<number, AudienceType> = {
	214: 'By Organization',
	35: 'By Property',
	634: 'By Role',
	676: 'By Group',
	784: 'By Individual'
};

const itemsLabelByAudience: Record<AudienceType, string> = {
	'By Organization': 'Organization selected',
	'By Property': 'Properties selected',
	'By Role': 'Roles selected',
	'By Group': 'Groups selected',
	'By Individual': 'Individuals selected',
	'Not selected': 'Selected'
};

interface ReviewCourse {
	id: number;
	title: string;
}

const Review: React.FC = () => {
	const {
		draft: { configure, customSetup }
	} = useStepper();

	const allCourses: ReviewCourse[] = IndividualCoursesData[0].courses.map(
		(course) => ({
			id: course.id,
			title: course.title
		})
	);

	const learningPathCourses = allCourses.filter((course) =>
		customSetup.selectedLearningPathCourseIds.includes(course.id)
	);
	const individualCourses = allCourses.filter((course) =>
		customSetup.selectedIndividualCourseIds.includes(course.id)
	);

	const selectedAudienceType =
		assignOptionById[customSetup.selectedAssignUsersOptionId ?? -1] ??
		'Not selected';

	const audienceNames = getAudienceNames(customSetup, selectedAudienceType);

	const itemsLabel = itemsLabelByAudience[selectedAudienceType];

	return (
		<div id="lms-ca-custom-review" className={styles.review}>
			<Heading
				title="Review & Confirm"
				subHeading="Review the configuration below and restart the assignment"
			/>

			<div
				id="lms-ca-custom-review-courses"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					Courses selected
				</div>
				<div className={styles.courseList}>
					{[
						...learningPathCourses.map((course, index) => ({
							id: course.id,
							label: course.title,
							icon: (
								<Icon type="learningPathOutline" role="img" />
							),
							tag: index === 0 ? 'Required' : 'Optional',
							tagVariant: index === 0 ? 'required' : 'optional'
						})),
						...individualCourses.map((course) => ({
							id: course.id,
							label: course.title,
							icon: <Icon type="indiCoursesOutline" role="img" />,
							tag: 'Required',
							tagVariant: 'required'
						}))
					].map((item, index) => (
						<div
							key={`${item.id}-${item.label}`}
							id={`lms-ca-custom-review-course-${index}`}
							className={styles.courseItem}
						>
							<div className={styles.courseItemLeft}>
								<div className={styles.serial}>{index + 1}</div>
								{item.icon}
								<div
									className={styles.courseLabel}
								>{`{${item.label}}`}</div>
								<div
									className={
										item.tagVariant === 'required'
											? styles.requiredTag
											: styles.optionalTag
									}
								>
									{item.tag}
								</div>
							</div>
						</div>
					))}
					{learningPathCourses.length === 0 &&
						individualCourses.length === 0 && (
							<div className={styles.empty}>
								No courses selected yet.
							</div>
						)}
				</div>
			</div>

			<div
				id="lms-ca-custom-review-audience"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					Full Campaign Audience
				</div>
				<div className={styles.summaryCard}>
					<div>
						<div className={styles.mutedLabel}>{itemsLabel}</div>
						<div className={styles.summaryValue}>
							{audienceNames.length
								? audienceNames.join(', ')
								: 'No audience selected'}
						</div>
					</div>
				</div>
			</div>

			<div
				id="lms-ca-custom-review-configure"
				className={styles.reviewSection}
			>
				<div className={styles.reviewSectionTitle}>
					Configure Settings
				</div>
				<ConfigureSettingsCard configure={configure} />
			</div>
		</div>
	);
};

const getAudienceNames = (
	customSetup: ReturnType<typeof useStepper>['draft']['customSetup'],
	type: AudienceType
) => {
	const pick = (source: { id: number; title: string }[], ids: number[]) =>
		source
			.filter((item) => ids.includes(item.id))
			.map((item) => item.title);

	switch (type) {
		case 'By Organization':
			return pick(
				organizationOptions,
				customSetup.selectedOrganizationIds
			);
		case 'By Property':
			return pick(propertyOptions, customSetup.selectedPropertyIds);
		case 'By Role':
			return pick(roleOptions, customSetup.selectedRoleIds);
		case 'By Group':
			return pick(groupOptions, customSetup.selectedGroupIds);
		case 'By Individual':
			return pick(
				individualOptions,
				customSetup.selectedIndividualUserIds
			);
		default:
			return [];
	}
};

export default Review;
